import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, ChevronDown, ExternalLink, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';

/**
 * PlatformLauncher — el botón "Plataformas" del header. OWNER-ONLY (Andy).
 *
 * Un solo botón para saltar a cualquier plataforma que construimos (Wolfsohn, Oxígeno,
 * medisoft Flom/AW, Modo Crear, Studio Dinamarca…) sin buscar el link ni la clave.
 * La lista es el catálogo de "Mis plataformas" (vive en el backend). Las que tienen el
 * puente configurado (`sso`) entran YA LOGUEADAS: el backend firma un pase de 60 s y de
 * un solo uso, y el destino lo canjea por su propia sesión. Las demás abren la URL.
 *
 * Para el resto del equipo sigue estando InstanceSwitcher (Wolfsohn); Layout decide cuál
 * se muestra.
 */
const pick = (v, lang) => (v && typeof v === 'object' ? (v[lang] || v.es || '') : v || '');

export default function PlatformLauncher() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language && i18n.language.startsWith('en') ? 'en' : 'es';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [launching, setLaunching] = useState(null);
  const wrapRef = useRef(null);
  const { shouldShow, dismiss } = useFeatureSpotlight('platform_launcher_v1');

  // Se carga recién al abrir: no hace falta pegarle al backend en cada página.
  useEffect(() => {
    if (!open || data) return;
    api.get('/my-platforms').then((r) => setData(r.data)).catch(() => toast.error(t('myPlatforms.loadFailed')));
  }, [open, data, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.platforms || [])
      .filter((p) => p.url)
      .filter((p) => !term || [p.name, p.client].some((v) => v && v.toLowerCase().includes(term)));
  }, [data, q]);

  const launch = useCallback(async (p) => {
    setLaunching(p.id);
    // La pestaña se abre ANTES del await: si se abre después, el navegador la toma
    // como popup no solicitado y la bloquea.
    const tab = window.open('', '_blank');
    try {
      const { data: r } = await api.post(`/my-platforms/${p.id}/launch`);
      if (!r?.url) throw new Error('sin url');
      if (tab) tab.location = r.url; else window.location.href = r.url;
      setOpen(false);
      setQ('');
    } catch {
      if (tab) tab.close();
      toast.error(t('platformLauncher.error'));
    } finally {
      setLaunching(null);
    }
  }, [t]);

  const groups = data?.groups || [];

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => { setOpen((v) => !v); if (shouldShow) dismiss(); }}
        data-tooltip={t('platformLauncher.tooltip')}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <LayoutGrid size={14} />
        {t('platformLauncher.button')}
        <ChevronDown size={12} />
      </button>
      <FeatureSpotlight
        anchorRef={wrapRef}
        show={shouldShow && !open}
        onDismiss={dismiss}
        title={t('platformLauncher.spotlightTitle')}
        body={t('platformLauncher.spotlightBody')}
        placement="bottom"
      />
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1000, margin: 0,
            width: 320, maxHeight: '70vh', overflowY: 'auto', padding: 8,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          }}
        >
          <input
            className="form-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && items[0]) launch(items[0]); }}
            placeholder={t('platformLauncher.searchPh')}
            style={{ marginBottom: 6 }}
          />
          {!data && <div style={{ padding: 8, fontSize: 13, color: 'var(--c-text-muted)' }}>{t('common.loading')}</div>}
          {data && !items.length && <div style={{ padding: 8, fontSize: 13, color: 'var(--c-text-muted)' }}>{t('myPlatforms.empty')}</div>}
          {groups.map((g) => {
            const list = items.filter((p) => p.group === g);
            if (!list.length) return null;
            return (
              <div key={g} style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--c-text-muted)', padding: '6px 8px 2px' }}>
                  {t(`myPlatforms.groups.${g}`)}
                </div>
                {list.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => launch(p)}
                    disabled={launching === p.id}
                    title={p.sso ? t('platformLauncher.ssoTitle') : pick(p.description, lang)}
                    style={{ width: '100%', justifyContent: 'flex-start', gap: 8, textAlign: 'left' }}
                  >
                    {p.sso
                      ? <KeyRound size={14} style={{ color: 'var(--c-resolved)', flexShrink: 0 }} />
                      : <ExternalLink size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />}
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    {p.sso && <span style={{ fontSize: 10, color: 'var(--c-resolved)', flexShrink: 0 }}>{t('platformLauncher.ssoBadge')}</span>}
                  </button>
                ))}
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid var(--c-border)', marginTop: 4, paddingTop: 6 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => { setOpen(false); navigate('/my-platforms'); }}
            >
              {t('platformLauncher.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
